import type { RouteLocationNormalizedLoaded } from "vue-router";
import type { ContentDocument, ContentRecord } from "../types/content";

const SITE_URL = "https://vpscope.com";
const SITE_NAME = "VPScope";

export interface SeoPayload {
  title: string;
  description: string;
  path: string;
  type?: "article" | "website";
  structuredData: Record<string, unknown>;
  language?: "zh-CN" | "en";
  alternates?: { lang: "zh-CN" | "en" | "x-default"; path: string }[];
}

function absoluteUrl(path: string) {
  return new URL(path, SITE_URL).href;
}

function setMeta(attribute: "name" | "property", key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  let element = window.document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = window.document.createElement("meta");
    element.setAttribute(attribute, key);
    window.document.head.append(element);
  }
  element.content = content;
}

export function applySeo(payload: SeoPayload) {
  const canonicalUrl = absoluteUrl(payload.path);
  window.document.title = payload.title;
  window.document.documentElement.lang = payload.language ?? "zh-CN";
  setMeta("name", "description", payload.description);
  setMeta("property", "og:title", payload.title);
  setMeta("property", "og:description", payload.description);
  setMeta("property", "og:url", canonicalUrl);
  setMeta("property", "og:type", payload.type ?? "website");
  setMeta("name", "twitter:card", "summary");
  setMeta("name", "twitter:title", payload.title);
  setMeta("name", "twitter:description", payload.description);

  let canonical = window.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = window.document.createElement("link");
    canonical.rel = "canonical";
    window.document.head.append(canonical);
  }
  canonical.href = canonicalUrl;

  window.document.head.querySelectorAll('link[data-vpscope-hreflang]').forEach((link) => link.remove());
  for (const alternate of payload.alternates ?? []) {
    const link = window.document.createElement("link");
    link.rel = "alternate";
    link.hreflang = alternate.lang;
    link.href = absoluteUrl(alternate.path);
    link.dataset.vpscopeHreflang = "true";
    window.document.head.append(link);
  }

  let jsonLd = window.document.head.querySelector<HTMLScriptElement>("#vpscope-structured-data");
  if (!jsonLd) {
    jsonLd = window.document.createElement("script");
    jsonLd.id = "vpscope-structured-data";
    jsonLd.type = "application/ld+json";
    window.document.head.append(jsonLd);
  }
  const serialized = JSON.stringify(payload.structuredData).replace(/</g, "\\u003c");
  jsonLd.text = serialized;
  jsonLd.textContent = serialized;
  jsonLd.innerHTML = serialized;
}

function webPage(type: "WebSite" | "WebPage" | "CollectionPage", name: string, description: string, path: string, language: "zh-CN" | "en" = "zh-CN") {
  return {
    "@context": "https://schema.org",
    "@type": type,
    name,
    description,
    url: absoluteUrl(path),
    inLanguage: language,
    isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
  };
}

export function routeSeo(route: RouteLocationNormalizedLoaded): SeoPayload {
  const english = route.path === "/en/" || route.path.startsWith("/en/");
  if (english) {
    const paired = route.path === "/en/" ? "/" : route.path.replace(/^\/en/, "");
    const alternate = [{ lang: "zh-CN" as const, path: paired }, { lang: "en" as const, path: route.path }, { lang: "x-default" as const, path: route.path }];
    const definitions: Record<string, SeoPayload> = {
      "en-home": { title: "VPScope | Practical server knowledge", description: "Reviewed guides and browser-local tools for deployment, troubleshooting, security, and infrastructure choices.", path: "/en/", language: "en", alternates: alternate, structuredData: webPage("WebSite", "VPScope", "Reviewed guides and browser-local tools for deployment, troubleshooting, security, and infrastructure choices.", "/en/", "en") },
      "en-library": { title: "VPScope | Practical knowledge", description: "Reviewed guides, historical VPS notes, and verified service entries for operators.", path: "/en/library", language: "en", alternates: alternate, structuredData: webPage("CollectionPage", "VPScope Library", "Reviewed guides, historical VPS notes, and verified service entries for operators.", "/en/library", "en") },
      "en-tools": { title: "VPScope | Tools and resources", description: "Small browser-local utilities plus carefully checked official entry points.", path: "/en/tools", language: "en", alternates: alternate, structuredData: webPage("CollectionPage", "VPScope Tools and resources", "Small browser-local utilities plus carefully checked official entry points.", "/en/tools", "en") },
      "en-about": { title: "VPScope | About the editorial method", description: "How VPScope makes editorial status, source boundaries, and review context visible.", path: "/en/about", language: "en", alternates: alternate, structuredData: webPage("WebPage", "About VPScope", "How VPScope makes editorial status, source boundaries, and review context visible.", "/en/about", "en") },
    };
    return definitions[String(route.name)] ?? definitions["en-home"];
  }
  const section = String(route.query.section ?? "");
  const definitions: Record<string, Omit<SeoPayload, "path">> = {
    home: {
      title: "VPScope | 技术知识与在线工具",
      description: "面向开发者与站长的中文技术知识、在线工具与 VPS 实践站。",
      structuredData: webPage("WebSite", "VPScope", "面向开发者与站长的中文技术知识、在线工具与 VPS 实践站。", "/"),
    },
    library: {
      title: `VPScope | ${section === "reviews" ? "VPS 测评" : section === "resources" ? "资源服务" : section === "knowledge" ? "技术知识" : "知识库"}`,
      description: "浏览已审核发布的技术指南、历史测评和资源服务内容。",
      structuredData: webPage("CollectionPage", "VPScope 知识库", "浏览已审核发布的技术指南、历史测评和资源服务内容。", "/library"),
    },
    tools: {
      title: "VPScope | 工具与资源",
      description: "本地运行的网络、配置与时间工具，以及已核对用途和边界的资源入口。",
      structuredData: webPage("CollectionPage", "VPScope 工具与资源", "本地运行的网络、配置与时间工具，以及已核对用途和边界的资源入口。", "/tools"),
    },
    "cidr-planner": toolSeo("CIDR 子网规划器", "把 IPv4 或 IPv6 前缀展开为准确边界，并按新前缀预览子网。", "/tools/cidr"),
    "data-workbench": toolSeo("JSON / YAML 工作台", "在浏览器本地校验、格式化并互相转换 JSON 与 YAML 配置数据。", "/tools/json-yaml"),
    "time-converter": toolSeo("时间戳与时区转换器", "在 Unix 时间、带偏移的标准时间和 IANA 本地钟表时间之间准确转换。", "/tools/time"),
    about: {
      title: "VPScope | 关于与编辑原则",
      description: "了解 VPScope 如何区分内容状态、证据边界和持续复核。",
      structuredData: webPage("WebPage", "关于 VPScope", "了解 VPScope 如何区分内容状态、证据边界和持续复核。", "/about"),
    },
  };
  const definition = definitions[String(route.name)] ?? definitions.home;
  const path = route.name === "library" ? "/library" : route.path;
  const englishPath = new Map([["/", "/en/"], ["/library", "/en/library"], ["/tools", "/en/tools"], ["/about", "/en/about"]]).get(path);
  return { ...definition, path, alternates: englishPath ? [{ lang: "zh-CN", path }, { lang: "en", path: englishPath }, { lang: "x-default", path: englishPath }] : undefined };
}

function toolSeo(name: string, description: string, path: string): Omit<SeoPayload, "path"> {
  return {
    title: `VPScope | ${name}`,
    description,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name,
      description,
      url: absoluteUrl(path),
      applicationCategory: "DeveloperApplication",
      operatingSystem: "Web",
      inLanguage: "zh-CN",
      isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
    },
  };
}

export function entrySeo(record: ContentRecord, document: ContentDocument): SeoPayload {
  const path = `/entry/${record.id}`;
  const type = document.kind === "guide" ? "TechArticle" : "Article";
  const structuredData: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": type,
    headline: record.title,
    description: document.description,
    url: absoluteUrl(path),
    inLanguage: "zh-CN",
    isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
  };
  if (record.published_at) structuredData.datePublished = record.published_at;
  if (document.last_reviewed_at) structuredData.dateModified = document.last_reviewed_at;
  return { title: `VPScope | ${record.title}`, description: document.description, path, type: "article", structuredData };
}
